import React, { useState } from 'react';
import { useBrowserExtensionDetected } from '../../../apis/workspace-registry';
import { Image } from '../../../../ui/Image';
import { Button } from '../../../components/Button';
import { FeaturesList } from './FeaturesList';
import { ExtensionDetectedSuccess } from './ExtensionDetectedSuccess';

import { useTranslation } from 'react-i18next';

interface ChromeWebStoreSetupProps {
  onComplete: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const ChromeWebStoreSetup: React.FC<ChromeWebStoreSetupProps> = ({
  onComplete,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const chromeWebStoreUrl =
    'https://chromewebstore.google.com/detail/pleri/obdhbjljlijoofbbdhfidiicfidbkhji';
  const [isInstalling, setIsInstalling] = useState(false);
  const {
    data: isExtensionDetected,
    isLoading: isCheckingExtension,
    refetch,
  } = useBrowserExtensionDetected();
  const handleInstall = () => {
    setIsInstalling(true);
    window.open(chromeWebStoreUrl, '_blank');
    setIsInstalling(false);
  };
  const handleComplete = async () => {
    try {
      await onComplete();
    } catch (error) {
      console.error('Failed to complete browser extension setup:', error);
    }
  };
  const handleCheckInstallation = async () => {
    try {
      await refetch();
      await handleComplete();
    } catch (error) {
      alert(
        'An error occurred while checking the extension installation. Please try again.',
      );
    }
  };
  const isDisabled = isInstalling || isLoading || isCheckingExtension;
  if (isExtensionDetected) {
    return <ExtensionDetectedSuccess />;
  }
  return (
    <div className="mx-auto max-w-2xl">
      {/* 1. Title */}
      <div className="mb-8 text-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 leading-tight">
          {t('chromeExtension')}
        </h2>
      </div>
      <div className="rounded-lg bg-white p-6 dark:bg-gray-900">
        {/* 2. Logo */}
        <div className="mb-8 text-center">
          <div className="relative mx-auto inline-block">
            <div className="absolute inset-0 animate-[pulse_4s_ease-in-out_infinite] rounded-xl bg-[#FF732E]/10 blur-xl" />
            <Image
              name={'chrome-extension'}
              type={'resourceType'}
              className="relative h-20 w-20 rounded-xl object-cover"
              alt={'chrome-extension'}
            />
          </div>
        </div>

        {/* 3. Short Description */}
        <div className="mb-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('whetherWeAreInAwsAzureGcp')}
          </p>
        </div>

        {/* 4. Learn More Section */}
        <div className="mb-8">
          <FeaturesList />
        </div>

        {/* 6. Call to Action Button */}
        <div className="mb-6 flex flex-col items-center">
          <Button
            onClick={handleInstall}
            disabled={isDisabled}
            variant="primary"
            className="px-8 py-3"
          >
            {isInstalling ? t('openingChromeWebStore') : t('installExtensionTitle')}
          </Button>
        </div>

        {/* 7. Cancel or Go Back Button */}
        <div className="flex flex-col items-center">
          <button
            onClick={onCancel}
            disabled={isDisabled}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            {t('common:cancel')}
          </button>
        </div>

        {/* Check Installation Button */}
        <div className="mt-8 flex flex-col items-center">
          <Button
            onClick={handleCheckInstallation}
            disabled={isDisabled}
            variant="secondary"
            className="px-8 py-3"
          >
            {isCheckingExtension
              ? t('checking')
              : t('iHaveInstalledTheExtension')}
          </Button>
        </div>
      </div>
    </div>
  );
};
